import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePricingConfigs } from '../../../hooks/useApi';
import { cn } from '../../../lib/utils';
import { AdminActiveStatusBadge } from './AdminActiveStatusBadge';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { LoadingState } from '../../LoadingSpinner';
import { formFieldGroupSurfaceClassName } from '../../ui/formPrimitives';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import {
    formatPricingConfigActivitiesSummary,
    formatPricingConfigDateTime,
    isPricingConfigPayload,
} from '../adminDashboardHelpers';
import { useAdminBreadcrumbBack } from '../../../hooks/useAdminBreadcrumbBack';
import { cardTitleClassName, pageTitleClassName, sectionTitleClassName } from '../../ui/typography';
import { InlineAlert } from '../../ui/inline-alert';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';

function DetailField({
    label,
    children,
    className,
}: {
    label: string;
    children: ReactNode;
    className?: string;
}) {
    return (
        <div className={cn(className)}>
            <p className="text-sm text-neutral-500">{label}</p>
            <div className="mt-1 font-medium text-neutral-950">{children}</div>
        </div>
    );
}

type PricingConfigUnavailableState = 'loading' | 'error' | 'missing';

function getPricingConfigUnavailableState(
    query: { isLoading: boolean; isError: boolean },
    found: boolean,
): PricingConfigUnavailableState | null {
    if (query.isLoading) return 'loading';
    if (query.isError) return 'error';
    return found ? null : 'missing';
}

function PricingConfigUnavailableCard({
    state,
    error,
    onRetry,
    isRetrying,
}: {
    state: PricingConfigUnavailableState;
    error?: unknown;
    onRetry?: () => void;
    isRetrying?: boolean;
}) {
    if (state === 'loading') {
        return (
            <Card>
                <LoadingState message="Loading pricing configuration…" />
            </Card>
        );
    }

    return (
        <Card>
            <CardContent className="space-y-4 p-8 text-sm text-neutral-600">
                {state === 'error' ? (
                    <InlineAlert
                        tone="error"
                        title="Couldn't load this pricing configuration"
                        onRetry={onRetry}
                        isRetrying={isRetrying}
                    >
                        {error instanceof Error ? error.message : null}
                    </InlineAlert>
                ) : (
                    <p>Pricing configuration not found.</p>
                )}
                <Button variant="outline" asChild>
                    <Link to="/admin">Back to admin</Link>
                </Button>
            </CardContent>
        </Card>
    );
}

const PRICING_UNIT_LABELS = {
    token: { unitLabel: 'Token', b2bCostLabel: 'B2B tokens', b2cCostLabel: 'B2C tokens' },
    credit: { unitLabel: 'Credit', b2bCostLabel: 'B2B credits', b2cCostLabel: 'B2C credits' },
} as const;

function getPricingUnitLabels(payload: { unitType?: string } | null) {
    const labels = PRICING_UNIT_LABELS[payload?.unitType === 'token' ? 'token' : 'credit'];
    return { ...labels, unitLabel: payload ? labels.unitLabel : null };
}

export function PricingConfigDetail() {
    const { pricingConfigId } = useParams<{ pricingConfigId: string }>();
    const { goBack } = useAdminBreadcrumbBack();
    const pricingQuery = usePricingConfigs(true);
    const pricingConfig =
        pricingConfigId !== undefined
            ? pricingQuery.data?.pricingConfigs.find((c) => c.id === pricingConfigId)
            : undefined;
    useDocumentTitle(pricingConfig?.name ?? 'Pricing configuration details');

    const unavailableState = getPricingConfigUnavailableState(
        pricingQuery,
        pricingConfigId !== undefined && pricingConfig !== undefined,
    );
    if (unavailableState || !pricingConfig) {
        return (
            <PricingConfigUnavailableCard
                state={unavailableState ?? 'missing'}
                error={pricingQuery.error}
                onRetry={() => void pricingQuery.refetch()}
                isRetrying={pricingQuery.isFetching}
            />
        );
    }

    const payload = isPricingConfigPayload(pricingConfig.config) ? pricingConfig.config : null;
    const { unitLabel, b2bCostLabel, b2cCostLabel } = getPricingUnitLabels(payload);

    return (
        <div className="space-y-6">
            <div className="flex items-start gap-3">
                <Button variant="ghost" size="icon" aria-label="Back" onClick={goBack}>
                    <ArrowLeft className="size-5" aria-hidden />
                </Button>
                <div className="min-w-0">
                    <p className="text-sm text-neutral-500">Pricing configuration</p>
                    <h1 className={pageTitleClassName}>{pricingConfig.name}</h1>
                    <p className="mt-1 text-sm text-neutral-500">ID: {pricingConfig.id}</p>
                </div>
            </div>

            <Card>
                <CardContent className="grid gap-6 p-6 md:grid-cols-2">
                    <DetailField label="Version">{pricingConfig.version}</DetailField>
                    <DetailField label="Status">
                        <AdminActiveStatusBadge isActive={pricingConfig.isActive} />
                    </DetailField>
                    <DetailField label="Pricing configuration group ID" className="md:col-span-2">
                        <span className="font-mono text-sm font-normal">
                            {pricingConfig.pricingConfigGroupId}
                        </span>
                    </DetailField>
                    <DetailField label="Created">
                        {formatPricingConfigDateTime(pricingConfig.createdAt)}
                    </DetailField>
                    <DetailField label="Last updated">
                        {formatPricingConfigDateTime(pricingConfig.updatedAt)}
                    </DetailField>
                    {pricingConfig.description ? (
                        <DetailField label="Description" className="md:col-span-2">
                            <span className="font-normal">{pricingConfig.description}</span>
                        </DetailField>
                    ) : null}
                </CardContent>
            </Card>

            <Card>
                <CardContent className="space-y-6 p-6">
                    <div>
                        <h2 className={sectionTitleClassName}>Pricing parameters</h2>
                        <p className="mt-1 text-sm text-neutral-500">
                            Values stored in this version&apos;s JSON configuration (Pro + Pulse).
                        </p>
                    </div>

                    {payload ? (
                        <>
                            <DetailField label="Unit type">{unitLabel}</DetailField>

                            <section className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}>
                                <div>
                                    <h3 className={cardTitleClassName}>Pro</h3>
                                    <p className="text-xs text-neutral-600">
                                        Estimates, B2B/B2C costs, sentiment map, and sample-size
                                        multipliers.
                                    </p>
                                </div>
                                <DetailField label="Supports pricing estimates">
                                    {payload.pro.supportsPricingEstimates ? 'Yes' : 'No'}
                                </DetailField>
                                <div className="grid gap-4 md:grid-cols-3">
                                    <DetailField label="Sentiment map multiplier">
                                        {payload.pro.sentimentMapMultiplier}
                                    </DetailField>
                                    <DetailField label={b2bCostLabel}>
                                        {payload.pro.b2bCredits}
                                    </DetailField>
                                    <DetailField label={b2cCostLabel}>
                                        {payload.pro.b2cCredits}
                                    </DetailField>
                                </div>
                                <div className="space-y-2">
                                    <p
                                        id="pricing-sample-sizes-label"
                                        className="text-sm text-neutral-500"
                                    >
                                        Sample sizes
                                    </p>
                                    <Table aria-labelledby="pricing-sample-sizes-label">
                                        <TableHeader className="bg-muted/55">
                                            <TableRow>
                                                <TableHead className="text-right">
                                                    Sample size (n)
                                                </TableHead>
                                                <TableHead className="text-right">
                                                    Multiplier
                                                </TableHead>
                                                <TableHead>
                                                    <span className="sr-only">Default</span>
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {[...payload.pro.sampleSizes]
                                                .slice()
                                                .sort((a, b) => a.n - b.n)
                                                .map((row) => {
                                                    const isDefault =
                                                        row.n === payload.pro.defaultSampleSize;
                                                    return (
                                                        <TableRow
                                                            key={row.n}
                                                            className={cn(
                                                                isDefault && 'bg-muted/35',
                                                            )}
                                                        >
                                                            <TableCell className="text-right font-medium">
                                                                {row.n}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                {row.multiplier}
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground">
                                                                {isDefault ? 'Default' : ''}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                    <p className="text-xs text-neutral-600">
                                        Default sample size (selected for new estimates):{' '}
                                        <span className="font-medium text-neutral-900">
                                            {payload.pro.defaultSampleSize}
                                        </span>
                                    </p>
                                </div>
                            </section>

                            <section className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}>
                                <div>
                                    <h3 className={cardTitleClassName}>Pulse</h3>
                                    <p className="text-xs text-neutral-600">
                                        Pricing inputs for standard Pulse usage.
                                    </p>
                                </div>
                                <DetailField label="Standard Pulse">
                                    {payload.pulse.standardPulse}
                                </DetailField>
                            </section>
                        </>
                    ) : (
                        <div className="space-y-4 rounded-md border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900 dark:bg-amber-950/40">
                            <div>
                                <p className="text-sm font-medium text-amber-950 dark:text-amber-100">
                                    Legacy or non-standard configuration shape
                                </p>
                                <p className="mt-1 text-sm text-amber-900/90 dark:text-amber-200/90">
                                    Summary (may be incomplete):{' '}
                                    <span className="font-medium">
                                        {formatPricingConfigActivitiesSummary(pricingConfig.config)}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-amber-900/80 dark:text-amber-300/80">
                                    Raw JSON
                                </p>
                                <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-white/90 p-3 font-mono text-xs text-neutral-800 ring-1 ring-inset ring-amber-200/80 dark:bg-neutral-950 dark:text-neutral-200 dark:ring-amber-900/60">
                                    {JSON.stringify(pricingConfig.config, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
